import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HTTPCONSTANTS } from '../http/http-constants';
import {
  PEBLPaymentLink,
  PEBLPaymentRequest,
  PEBLPaymentResponse,
  PaymentAuditEvent,
  PaymentLinkList,
  PeblScheduleStatus,
  ScheduleItem,
  ScheduleListResponse,
  ScheduleOccurrenceListResponse,
  ScheduleResponse,
  TransactionRecharge,
  TransactionRefund,
  TransactionReverse,
  VTPaymentRequest,
  VTPaymentResponse,
} from '@odin/odin-transactions';
import { MerchantService } from './merchant.service';
import {
  TransactionComplete
} from "../../../../odin-transactions/src/lib/transactions-layout/models/transaction-complete";

@Injectable({ providedIn: 'root' })
export class VTService {
  constructor(
    private http: HttpClient,
    private merchantService: MerchantService,
  ) {}

  public SubmitPaymentRequest(
    data: VTPaymentRequest,
  ): Observable<VTPaymentResponse> {
    const url = `${HTTPCONSTANTS.protocol}${
      HTTPCONSTANTS.odinBaseAddress
    }/transactions/v1/VT/${this.merchantService.getMerchantRef()}`;
    return this.http.post<VTPaymentResponse>(url, data);
  }

  public SubmitRefundRequest(
    data: TransactionRefund,
    monekId?: string,
  ): Observable<VTPaymentResponse> {
    const url = `${HTTPCONSTANTS.protocol}${
      HTTPCONSTANTS.odinBaseAddress
    }/transactions/v1/VT/${this.merchantService.getMerchantRef()}/refund${this.monekIdQuery(monekId)}`;
    return this.http.post<VTPaymentResponse>(url, data);
  }

  public SubmitCompleteRequest(
    data: TransactionComplete,
  ): Observable<VTPaymentResponse> {
    const url = `${HTTPCONSTANTS.protocol}${
      HTTPCONSTANTS.odinBaseAddress
    }/transactions/v1/VT/${this.merchantService.getMerchantRef()}/complete`;
    return this.http.post<VTPaymentResponse>(url, data);
  }

  public SubmitRechargeRequest(
    data: TransactionRecharge,
    monekId?: string,
  ): Observable<VTPaymentResponse> {
    const url = `${HTTPCONSTANTS.protocol}${
      HTTPCONSTANTS.odinBaseAddress
    }/transactions/v1/VT/${this.merchantService.getMerchantRef()}/recharge${this.monekIdQuery(monekId)}`;
    return this.http.post<VTPaymentResponse>(url, data);
  }

  /** Builds the optional `?monekId=` query used to pick a specific MID on the business unit. */
  private monekIdQuery(monekId?: string): string {
    const trimmed = (monekId ?? '').trim();
    return trimmed ? `?monekId=${encodeURIComponent(trimmed)}` : '';
  }

  public SubmitReverseRequest(
    data: TransactionReverse,
  ): Observable<VTPaymentResponse> {
    const url = `${HTTPCONSTANTS.protocol}${
      HTTPCONSTANTS.odinBaseAddress
    }/transactions/v1/VT/${this.merchantService.getMerchantRef()}/reverse`;
    return this.http.post<VTPaymentResponse>(url, data);
  }

  public CreatePeblRequest(
    data: PEBLPaymentRequest,
  ): Observable<PEBLPaymentResponse | ScheduleResponse> {
    const url = `${HTTPCONSTANTS.protocol}${
      HTTPCONSTANTS.baseAddress
    }/payment/${this.merchantService.getMerchantRef()}`;
    return this.http.post<PEBLPaymentResponse | ScheduleResponse>(url, data);
  }

  public GetPeblLinks(
    filters: { startDate?: Date; endDate?: Date; status?: string },
    lastEvaluatedKey?: string,
  ): Observable<PaymentLinkList> {
    const filterList = [];
    if (filters.startDate != undefined)
      filterList.push(`minDate=${filters.startDate.toISOString()}`);
    if (filters.endDate != undefined) {
      filterList.push(`maxDate=${filters.endDate.toISOString()}`);
      if (filters.startDate == undefined)
        filterList.push(`minDate=${new Date('1990-01-01').toISOString()}`);
    } else {
      // end date required if start date is set
      if (filters.startDate != undefined) {
        const endDate = new Date();
        endDate.setHours(23);
        endDate.setMinutes(59);
        endDate.setSeconds(59);
        filterList.push(`maxDate=${endDate.toISOString()}`);
      }
    }
    if (filters.status != undefined)
      filterList.push(`status=${filters.status}`);
    const filterQuery = filterList.join('&');
    const url = `${HTTPCONSTANTS.protocol}${
      HTTPCONSTANTS.baseAddress
    }/payment/${this.merchantService.getMerchantRef()}${
      lastEvaluatedKey === undefined || lastEvaluatedKey === ''
        ? `?${filterQuery}`
        : `?lastEvaluatedKey=${lastEvaluatedKey}&${filterQuery}`
    }`;
    return this.http.get<PaymentLinkList>(url);
  }

  public CancelPaymentLink(
    paymentLink: PEBLPaymentLink,
    reason: string,
  ): Observable<{ message: string }> {
    const url = `${HTTPCONSTANTS.protocol}${
      HTTPCONSTANTS.baseAddress
    }/payment/${this.merchantService.getMerchantRef()}?paymentReference=${
      paymentLink.paymentReference
    }&cancellationReason=${reason}`;
    return this.http.delete<{ message: string }>(url);
  }

  public GetSchedules(
    status?: PeblScheduleStatus,
    lastEvaluatedKey?: string,
    take: number = 25, // backend hard-caps at 25
  ): Observable<ScheduleListResponse> {
    const params: string[] = [`take=${take}`];
    if (status) params.push(`status=${status}`);
    if (lastEvaluatedKey)
      params.push(`lastEvaluatedKey=${encodeURIComponent(lastEvaluatedKey)}`);
    const url = `${HTTPCONSTANTS.protocol}${
      HTTPCONSTANTS.baseAddress
    }/payment/${this.merchantService.getMerchantRef()}/schedules?${params.join('&')}`;
    return this.http.get<ScheduleListResponse>(url);
  }

  public GetSchedule(scheduleReference: string): Observable<ScheduleItem> {
    const url = `${HTTPCONSTANTS.protocol}${
      HTTPCONSTANTS.baseAddress
    }/payment/${this.merchantService.getMerchantRef()}/schedules/${scheduleReference}`;
    return this.http.get<ScheduleItem>(url);
  }

  public GetScheduleOccurrences(
    scheduleReference: string,
    lastEvaluatedKey?: string,
    take: number = 25, // backend hard-caps at 25
  ): Observable<ScheduleOccurrenceListResponse> {
    const params: string[] = [`take=${take}`];
    if (lastEvaluatedKey)
      params.push(`lastEvaluatedKey=${encodeURIComponent(lastEvaluatedKey)}`);
    const url = `${HTTPCONSTANTS.protocol}${
      HTTPCONSTANTS.baseAddress
    }/payment/${this.merchantService.getMerchantRef()}/schedules/${scheduleReference}/occurrences?${params.join('&')}`;
    return this.http.get<ScheduleOccurrenceListResponse>(url);
  }

  public GetOccurrenceAuditHistory(
    scheduleReference: string,
    paymentReference: string,
  ): Observable<PaymentAuditEvent[]> {
    const url = `${HTTPCONSTANTS.protocol}${
      HTTPCONSTANTS.baseAddress
    }/payment/${this.merchantService.getMerchantRef()}/schedules/${scheduleReference}/occurrences/${paymentReference}/audit-history`;
    return this.http.get<PaymentAuditEvent[]>(url);
  }

  public CancelSchedule(
    scheduleReference: string,
    reason: string,
  ): Observable<{ message: string }> {
    const url = `${HTTPCONSTANTS.protocol}${
      HTTPCONSTANTS.baseAddress
    }/payment/${this.merchantService.getMerchantRef()}/schedules/${scheduleReference}?cancellationReason=${encodeURIComponent(
      reason,
    )}`;
    return this.http.delete<{ message: string }>(url);
  }
}
